import { Info, XCircle } from "lucide-react";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from "../../components/misc/Card";
import type { DashboardMetrics } from "./types";
import type { DashboardMetrics as SyncMetrics } from "../listing-sync/types";
import { useState } from "react";

interface MetricCardsProps {
    metrics: DashboardMetrics | SyncMetrics;
    isMobile: boolean;
    sync?: boolean;
}

const infoMessages: Record<keyof DashboardMetrics, string> = {
    scheduledCount:
        "The number of campaigns that are scheduled to renew in the upcoming period.",
    successCount:
        "The total number of campaigns that have successfully completed their renewal process.",
    failedCount:
        "The total number of campaign renewals that encountered errors or failed to complete.",
    cancelledCount:
        "The number of campaign renewals that were cancelled by user action or system decision.",
    averageDuration:
        "The average time, in seconds, taken for all successful campaign renewals to complete.",
    allActiveCampaigns: "The total number of active campaigns in the system",
};

const listingSyncInfoMessages: Record<keyof SyncMetrics, string> = {
    scheduledCount:
        "The number of listings queued for syncing in the upcoming period.",
    successCount:
        "The total number of listing syncs that have successfully completed.",
    failedCount:
        "The total number of listing syncs that encountered errors or failed to complete.",
    cancelledCount:
        "The number of listing syncs that were cancelled by user action or system decision.",
    averageDuration:
        "The average time, in seconds, taken for all successful listing syncs to complete.",
};

export function MetricCards({
    metrics,
    isMobile,
    sync = false,
}: MetricCardsProps) {
    const [infoMetric, setInfoMetric] = useState<keyof DashboardMetrics | null>(
        null
    );

    const matching =
        (metrics as DashboardMetrics)?.allActiveCampaigns ===
        metrics?.scheduledCount;

    return (
        <>
            <div
                className={`grid gap-4 grid-cols-${
                    isMobile ? "2" : sync ? "5" : "6"
                }`}
            >
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-semibold">
                            Scheduled {sync ? "Syncs" : "Renewals"}
                        </CardTitle>
                        <Info
                            className="h-4 w-4 text-secondary cursor-pointer"
                            onClick={() => setInfoMetric("scheduledCount")}
                        />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">
                            {metrics.scheduledCount.toLocaleString()}
                        </div>
                        <p className="text-xs text-muted-foreground">
                            Upcoming{" "}
                            {sync ? "listing syncs" : "campaign renewals"}
                        </p>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-semibold">
                            Successful {sync ? "Syncs" : "Renewals"}
                        </CardTitle>
                        <Info
                            className="h-4 w-4 text-secondary cursor-pointer"
                            onClick={() => setInfoMetric("successCount")}
                        />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">
                            {metrics.successCount.toLocaleString()}
                        </div>
                        <p className="text-xs text-muted-foreground">
                            {(
                                (metrics.successCount /
                                    (metrics.successCount +
                                        metrics.failedCount)) *
                                100
                            ).toFixed(1)}
                            % success rate
                        </p>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-semibold">
                            Failed {sync ? "Syncs" : "Renewals"}
                        </CardTitle>
                        <Info
                            className="h-4 w-4 text-secondary cursor-pointer"
                            onClick={() => setInfoMetric("failedCount")}
                        />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">
                            {metrics.failedCount.toLocaleString()}
                        </div>
                        <p className="text-xs text-muted-foreground">
                            {(
                                (metrics.failedCount /
                                    (metrics.successCount +
                                        metrics.failedCount)) *
                                100
                            ).toFixed(1)}
                            % failure rate
                        </p>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-semibold">
                            Cancelled {sync ? "Syncs" : "Renewals"}
                        </CardTitle>
                        <Info
                            className="h-4 w-4 text-secondary cursor-pointer"
                            onClick={() => setInfoMetric("cancelledCount")}
                        />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">
                            {metrics.cancelledCount}
                        </div>
                        <p className="text-xs text-muted-foreground">
                            Cancelled{" "}
                            {sync ? "listing syncs" : "campaign renewals"}
                        </p>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-semibold">
                            Average {sync ? "Sync" : "Renewal"} Duration
                        </CardTitle>
                        <Info
                            className="h-4 w-4 text-secondary cursor-pointer"
                            onClick={() => setInfoMetric("averageDuration")}
                        />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">
                            {(metrics.averageDuration / 1000).toFixed(2)} s
                        </div>
                        <p className="text-xs text-muted-foreground">
                            Average time taken for renewals
                        </p>
                    </CardContent>
                </Card>
                {!sync && (
                    <Card
                        className={`${
                            matching ? "bg-green-500" : "bg-red-600"
                        } text-white`}
                    >
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                            <CardTitle className="text-sm font-semibold text-white">
                                Active Campaign Count
                            </CardTitle>
                            <Info
                                className="h-4 w-4 cursor-pointer"
                                onClick={() =>
                                    setInfoMetric("allActiveCampaigns")
                                }
                            />
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold">
                                {
                                    (metrics as DashboardMetrics)
                                        .allActiveCampaigns
                                }
                            </div>
                            <p className="text-xs text-muted-foreground">
                                Current Active Campaigns
                            </p>
                        </CardContent>
                    </Card>
                )}
            </div>

            {infoMetric && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-6 rounded-lg max-w-md w-full relative">
                        <button
                            className="absolute top-2 right-2"
                            onClick={() => setInfoMetric(null)}
                        >
                            <XCircle className="h-5 w-5 text-red-500" />
                        </button>
                        <h2 className="text-lg font-semibold mb-2 text-primary">
                            {infoMetric
                                .replace(/([A-Z])/g, " $1")
                                .trim()
                                .replace(/^./, (char) => char.toUpperCase())}
                        </h2>
                        <p className="text-sm text-gray-700">
                            {sync
                                ? listingSyncInfoMessages[
                                      infoMetric as keyof SyncMetrics
                                  ]
                                : infoMessages[
                                      infoMetric as keyof DashboardMetrics
                                  ]}
                        </p>
                    </div>
                </div>
            )}
        </>
    );
}
